<template>
  <v-container fluid>
    <v-alert-dialog ref="alert" />

    <v-data-table
      :headers="headers"
      :items="items"
      :loading="loading"
      no-data-text="No hay solicitudes de cambio registradas."
      loading-text="Cargando solicitudes..."
      class="elevation-0"
    >
      <template v-slot:[`item.empleado`]="{ item }">
        <div class="font-weight-medium">{{ item.empleado || 'N/D' }}</div>
        <div class="text-caption grey--text">
          <span v-if="item.codigo_empleado">{{ item.codigo_empleado }} · </span>
        </div>
      </template>

      <template v-slot:[`item.puesto`]="{ item }">
        <div>{{ item.nombre_puesto || item.puesto || 'N/D' }}</div>
        <div class="text-caption grey--text">{{ item.intervalo || construirIntervalo(item) }}</div>
      </template>

      <template v-slot:[`item.informacion`]="{ item }">
        <v-chip
          v-for="id in item.ids_campos_adicionales || []"
          :key="`${item.id_solicitud_cambio_expediente}-${id}`"
          x-small
          outlined
          class="mr-1 mb-1"
        >
          {{ nombreCampoAdicional(id) }}
        </v-chip>
        <v-chip
          v-for="requisito in requisitosSolicitud(item)"
          :key="`${item.id_solicitud_cambio_expediente}-${requisito.id_puesto_expediente_requisito}`"
          x-small
          outlined
          class="mr-1 mb-1"
        >
          {{ requisito.nombre || requisito.codigo || 'Requisito' }}
        </v-chip>
        <span v-if="!requisitosSolicitud(item).length && !(item.ids_campos_adicionales || []).length">N/D</span>
      </template>

      <template v-slot:[`item.motivo`]="{ item }">
        <span class="motivo-solicitud">{{ item.motivo || 'Sin motivo indicado' }}</span>
      </template>

      <template v-slot:[`item.estado_nombre`]="{ item }">
        <v-chip small :color="estadoColor(item.estado)" text-color="white">
          {{ item.estado_nombre || 'N/D' }}
        </v-chip>
        <div v-if="item.resuelto_por" class="text-caption grey--text mt-1">
          {{ item.resuelto_por }}<span v-if="item.resuelto"> · {{ item.resuelto }}</span>
        </div>
      </template>

      <template v-slot:[`item.creado`]="{ item }">
        {{ item.creado }}
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-tooltip v-if="Number(item.estado) === 1" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon small color="primary" v-bind="attrs" v-on="on" @click="openResolverDialog(item)">
              <v-icon small>mdi-file-check-outline</v-icon>
            </v-btn>
          </template>
          <span>Resolver solicitud</span>
        </v-tooltip>
        <span v-else>—</span>
      </template>
    </v-data-table>

    <v-dialog v-model="dialogResolver" max-width="600px" persistent>
      <v-card>
        <v-card-title>Resolver solicitud de cambio</v-card-title>
        <v-card-text>
          <v-sheet v-if="solicitudSeleccionada" outlined rounded class="pa-4 mb-5 solicitud-resumen">
            <v-row dense>
              <v-col cols="12" sm="7">
                <div class="text-caption text-uppercase grey--text">Empleado</div>
                <div class="font-weight-medium">{{ solicitudSeleccionada.empleado || 'N/D' }}</div>
                <div v-if="solicitudSeleccionada.codigo_empleado" class="text-caption grey--text">
                  {{ solicitudSeleccionada.codigo_empleado }}
                </div>
              </v-col>
              <v-col cols="12" sm="5">
                <div class="text-caption text-uppercase grey--text">Puesto</div>
                <div>{{ solicitudSeleccionada.nombre_puesto || solicitudSeleccionada.puesto || 'N/D' }}</div>
                <div class="text-caption grey--text">{{ solicitudSeleccionada.intervalo || construirIntervalo(solicitudSeleccionada) }}</div>
              </v-col>

              <v-col cols="12" sm="7" class="mt-3">
                <div class="text-caption text-uppercase grey--text">Solicitada por</div>
                <div>{{ solicitudSeleccionada.solicitado_por || 'N/D' }}</div>
              </v-col>
              <v-col cols="12" sm="5" class="mt-3">
                <div class="text-caption text-uppercase grey--text">Fecha de solicitud</div>
                <div>{{ solicitudSeleccionada.creado }}</div>
              </v-col>

              <v-col cols="12" class="mt-3">
                <div class="text-caption text-uppercase grey--text">Motivo</div>
                <div class="solicitud-motivo-detalle">{{ solicitudSeleccionada.motivo || 'Sin motivo indicado' }}</div>
              </v-col>

              <v-col cols="12" class="mt-3">
                <div class="text-caption text-uppercase grey--text mb-1">Información que solicita cambiar</div>
                <v-chip
                  v-for="requisito in requisitosSolicitud(solicitudSeleccionada)"
                  :key="`dialog-${requisito.id_puesto_expediente_requisito}`"
                  small
                  outlined
                  class="mr-1 mb-1"
                >
                  {{ requisito.nombre || requisito.codigo || 'Requisito' }}
                </v-chip>
                <v-chip
                  v-for="id in solicitudSeleccionada.ids_campos_adicionales || []"
                  :key="`dialog-campo-${id}`"
                  small
                  outlined
                  class="mr-1 mb-1"
                >
                  {{ nombreCampoAdicional(id) }}
                </v-chip>
                <span v-if="!requisitosSolicitud(solicitudSeleccionada).length && !(solicitudSeleccionada.ids_campos_adicionales || []).length">
                  N/D
                </span>
              </v-col>
            </v-row>
          </v-sheet>

          <div class="text-subtitle-2 mb-2">Decisión</div>
          <div class="text-center mb-4">
            <v-btn-toggle v-model="aceptar" mandatory>
              <v-btn :value="true" color="green" outlined>
                <v-icon left small color="green">mdi-check</v-icon>
                Aceptar
              </v-btn>
              <v-btn :value="false" color="red" outlined>
                <v-icon left small color="red">mdi-close</v-icon>
                Rechazar
              </v-btn>
            </v-btn-toggle>
          </div>

          <v-textarea
            v-model="comentario"
            label="Comentario de resolución"
            outlined
            rows="2"
            auto-grow
            counter="500"
            :disabled="saving"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text color="green" :disabled="saving" @click="closeResolverDialog">Cancelar</v-btn>
          <v-btn text color="red" :loading="saving" @click="resolverSolicitud">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'
import VAlertDialog from '@/components/VAlertDialog.vue'

export default {
  components: { VAlertDialog },
  data: () => ({
    loading: false,
    saving: false,
    items: [],
    dialogResolver: false,
    solicitudSeleccionada: null,
    aceptar: null,
    comentario: '',
    headers: [
      { text: 'Empleado', value: 'empleado' },
      { text: 'Puesto', value: 'puesto' },
      { text: 'Información a cambiar', value: 'informacion', sortable: false, width: '30%' },
      { text: 'Motivo', value: 'motivo', sortable: false },
      { text: 'Solicitud', value: 'creado' },
      { text: 'Estado', value: 'estado_nombre', align: 'center' },
      { text: 'Acciones', value: 'actions', sortable: false, align: 'center' }
    ],
    camposAdicionales: {
      1: 'Estado civil',
      2: 'Número de Seguridad Social',
      3: 'Datos de contacto',
      4: 'Información legal/fiscal',
      5: 'Cuentas bancarias'
    }
  }),
  mounted() {
    this.getItems()
  },
  methods: {
    async getItems() {
      if (this.loading) return
      this.loading = true
      try {
        const response = await axios.get('/RecursosHumanos/ListarSolicitudesCambioExpediente')
        const solicitudes = this.getResponseData(response)
        this.items = await this.agregarRequisitosSolicitudes(solicitudes)
      } catch (error) {
        this.showError(error, 'No fue posible cargar las solicitudes de cambio.')
      } finally {
        this.loading = false
      }
    },
    openResolverDialog(solicitud) {
      this.solicitudSeleccionada = solicitud
      this.aceptar = null
      this.comentario = ''
      this.dialogResolver = true
    },
    closeResolverDialog() {
      this.dialogResolver = false
      this.solicitudSeleccionada = null
      this.aceptar = null
      this.comentario = ''
    },
    async resolverSolicitud() {
      if (!this.solicitudSeleccionada) return
      this.saving = true
      try {
        await axios.put(
          `/RecursosHumanos/ResolverSolicitudCambioExpediente/${this.solicitudSeleccionada.id_solicitud_cambio_expediente}`,
          { aceptar: this.aceptar, comentario: this.comentario.trim() }
        )
        this.closeResolverDialog()
        //this.$refs.alert.setMessage('', 'La solicitud fue resuelta correctamente.')
        //this.$refs.alert.open()
        this.getItems()
      } catch (error) {
        //this.showError(error, 'No fue posible resolver la solicitud de cambio.')
      } finally {
        this.saving = false
      }
    },
    async agregarRequisitosSolicitudes(solicitudes) {
      const puestosIds = [...new Set(solicitudes.map(item => item.id_empleado_puesto).filter(Boolean))]
      const requisitosPorPuesto = {}

      await Promise.all(puestosIds.map(async idEmpleadoPuesto => {
        try {
          const response = await axios.get(`/RecursosHumanos/ListarRequisitosEmpleadoPuesto/${idEmpleadoPuesto}`)
          requisitosPorPuesto[idEmpleadoPuesto] = this.getResponseData(response)
        } catch (error) {
          requisitosPorPuesto[idEmpleadoPuesto] = []
        }
      }))

      return solicitudes.map(solicitud => ({
        ...solicitud,
        requisitos_solicitados: (requisitosPorPuesto[solicitud.id_empleado_puesto] || []).filter(requisito =>
          (solicitud.ids_puesto_expediente_requisitos || []).includes(requisito.id_puesto_expediente_requisito)
        )
      }))
    },
    requisitosSolicitud(solicitud) {
      return solicitud.requisitos_solicitados || []
    },
    nombreCampoAdicional(id) {
      return this.camposAdicionales[id] || `Campo ${id}`
    },
    construirIntervalo(solicitud) {
      if (!solicitud.desde && !solicitud.hasta) return 'N/D'
      return `${solicitud.desde || 'N/D'} - ${solicitud.hasta || 'Indefinido'}`
    },
    estadoColor(estado) {
      if (Number(estado) === 1) return 'orange'
      if (Number(estado) === 2) return 'green'
      if (Number(estado) === 3) return 'red'
      if (Number(estado) === 4) return 'blue'
      return 'grey'
    },
    getResponseData(response) {
      const data = response && response.data
      if (Array.isArray(data)) return data
      if (data && Array.isArray(data.data)) return data.data
      if (data && Array.isArray(data.items)) return data.items
      return []
    },
    showError(error, fallback) {
      const data = error && error.response && error.response.data
      const message = (data && (data.msg || data.message || data.title)) || fallback
      this.$refs.alert.setMessage('', message)
      this.$refs.alert.open()
    }
  }
}
</script>

<style scoped>
.motivo-solicitud {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.solicitud-resumen {
  background-color: #fafafa;
}

.solicitud-motivo-detalle {
  white-space: pre-line;
}
</style>
